import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import classNames from 'classnames';
import Color from 'color';
import React from 'react';
import { guid, UTC, utc } from 'rewire-common';
import { Observe, version } from 'rewire-core';
import { createColumn, createGrid, IColumn, IRowData } from 'rewire-grid';
import { DraggableResizableDialog } from 'rewire-ui';
import { IUdf } from '../DataTypes';
import EditDialogModel from '../schedule/EditDialogModel';
import UdfsModel, { refEquals } from '../schedule/UdfsModel';
import Grid from '../ui/Grid';
import { makeStyles, Theme } from '../utility/styles';
import DefaultEditFormView from './DefaultEditFormView';
import { useWatch } from './SettingsModel';
import UdfAddMenu from './UdfAddMenu';

const cardStyle = (theme: Theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    flex: '1 1 auto',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    flex: 4,
  },
  right: {
    display: 'flex',
    padding: 4,
    minWidth: 350,
    flex: 1,
  },
  subTitle: {
    color: theme.palette.primary.main,
  },
  grid: {
    display: 'grid',
    flex: 1,
    gridTemplateColumns: 'repeat(auto-fill, minmax(375px, 1fr))',
    gridTemplateRows: 'repeat(auto-fill, minmax(150px, 1fr))',
    gridGap: '12px',
  },
  cardIcon: {
    width: '3rem',
    height: '3rem',
    color: theme.palette.grey['500'],
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  delete: {
    marginLeft: 'auto',
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 12px 0 12px',
    '&.selected': {
      backgroundColor: '#dfeff5',
    },
    '&.archived': {
      // backgroundColor: '#eee',
      textDecoration: 'line-through',
    },
  },
  cardContent: {
    flex: '1',
    overflow: 'hidden',
    padding: 0,
  },
  panelIconButton: {
    margin: '0px 10px',
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      color: Color(theme.palette.error.main).darken(0.3).toString(),
    },
  },
  panelIcon: {
    flex: '0 0 auto',
    fontSize: 24,
    fontWeight: 'bold',
  },
  disabled: {
    color: 'lightgray !important',
    fontStyle: 'italic',
  },
  title: {
    display: 'flex',
    flex: '1',
    alignSelf: 'center',
  },
  avatar: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: 60,
    height: 60,
  },
  pagination: {
    padding: 0,
    height: 36,
    minHeight: 36,
    marginTop: -8,
    marginLeft: 'auto',
    fontSize: 14,
    '& span': {
      fontSize: 14,
    },
    flex: '0 0 auto',
  },
  gridWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: 4,
    paddingBottom: 8,
    paddingLeft: 4,
    paddingRight: 8,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingBottom: 8,
    paddingTop: 0,
    '& tr.archived': {
      textDecoration: 'line-through',
    },
  },
  paper: {
    flex: 1,
    padding: '0px 8px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowY: 'auto',
  },
});

export const useCardStyle = makeStyles(cardStyle);

const udfStyle = (theme: Theme) => ({
  root: {
    display: 'flex',
    height: '100%',
  },
  left: {
    flex: '0 0 250px',
    margin: 8,
    border: `1px solid ${theme.palette.grey['300']}`,
    overflowY: 'auto',
  },
  title: {
    display: 'flex',
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& h6': { flex: 1 },
  },
  heading: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    flexDirection: 'column',
    margin: '8px 8px 0px 8px',
    padding: '0px 5px 0px 5px',
    '& > h6': {
      width: '100%',
    },
    '& > hr': {
      marginTop: '8px',
      marginBottom: '0px',
      width: '100%',
      border: '1px solid #eee',
    },
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  childrenContainer: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  addIcon: {
    color: theme.palette.dialogPrimary.main,
    flex: '0 0 auto',
    '&:hover': {
      color: Color(theme.palette.dialogPrimary.main).lighten(0.35).string(),
    },
  },
  right: {
    flex: '1',
    minWidth: '250px',
    flexDirection: 'column',
    display: 'flex',
  },
  formButtonsRoot: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  formButtonsButton: {
    minWidth: '90px',
  },
});

export function createReferenceListWithEffectiveRange(searcher: any, _udf: IUdf) {
  const gridOptions = {
    clearSelectionOnBlur: false,
    rowHeight: 28,
    headerRowHeight: 24,
  };

  const cols: IColumn[] = [
    createColumn('name', 'Name', {
      width: '125px',
      canSort: true,
      visible: true,
      type: { type: 'auto-complete', options: searcher },
    }),
    createColumn('start', 'Start', { width: '75px', canSort: true, visible: true, type: { type: 'date' } }),
    createColumn('end', 'End', { width: '75px', canSort: false, visible: true, type: { type: 'date' } }),
  ];

  const grid = createGrid([], cols, { ...gridOptions });

  const Editor = (props: any) => {
    const classes = useUdfStyle();
    if (props.disabled) grid.readOnly = true;
    grid.set((props.value ?? []).map((r: any) => ({ id: r.id, data: { ...r } }) as IRowData));

    function newRow() {
      const id = guid();
      grid.addRow({ id, data: { id, start: utc().startOfDay(), end: utc(UTC.MaxValue) } });
    }

    useWatch(
      () => {
        for (const e of grid.rows) {
          version(e.data);
        }
      },
      () => {
        const value: any[] = [];
        for (const row of grid.rows) {
          if (row?.data) {
            const v = {
              equals: refEquals,
              start: row.data.start ? utc(row.data.start) : utc(UTC.MinValue),
              end: row.data.end ? utc(row.data.end) : utc(UTC.MaxValue),
              name: row.data.name ? { ...row.data.name } : undefined,
            };
            value.push(v);
          }
        }
        requestAnimationFrame(() => {
          props.onValueChange(value);
        });
      },
    );

    return (
      <>
        <Box display="flex" alignItems="center" marginBottom="4px">
          <span style={{ fontSize: 10, color: '#0000008a', flex: '1' }}>{props.label}</span>
          {!props.disabled && (
            <IconButton disabled={props.disabled} onClick={newRow}>
              <AddIcon className={classes.addIcon} />
            </IconButton>
          )}
        </Box>
        <Grid grid={grid} {...props} />
      </>
    );
  };
  return Editor;
}

const useUdfStyle = makeStyles(udfStyle);
type ISelectable<T> = T & { selected: boolean };

const renderTitleFn = (editor: EditDialogModel, udf: UdfsModel) => (): JSX.Element => {
  const classes = useUdfStyle();
  const cardStyles = useCardStyle();

  return (
    <div className={classes.title}>
      <div>{editor.title}</div>
      <div className={classes.actionsContainer}>
        <IconButton
          className={classNames(cardStyles.panelIconButton, cardStyles.deleteButton)}
          title="Delete"
          arial-label="Delete"
          onClick={udf.removeItem}
          disabled={!udf.canDelete}
        >
          <DeleteIcon className={classNames(cardStyles.panelIcon, 'delete')} />
        </IconButton>
        <UdfAddMenu udfsModel={udf} />
      </div>
    </div>
  );
};

export default ({ udfEditor }: { udfEditor?: UdfsModel }) => {
  const classes = useUdfStyle();
  const editor = udfEditor?.editor;
  const form = editor?.form;
  // watch for the type value change and swap in the drop down list if a reference type is selected.
  useWatch(
    () => form?.field?.['type']?.value,
    () => {
      if (!form) return;
      const typeValue = form.field['type'].value;
      form.field['allowableValues'].visible = !(typeValue === 'reference' || typeValue === 'boolean' || typeValue === 'expression');
      form.field['referenceValues'].visible = typeValue === 'reference';
      form.field['expressionType'].visible = typeValue === 'expression';
      form.field['hasEffective'].visible = typeValue === 'reference';
      form.field['expression'].visible = typeValue === 'expression';
      form.field['isList'].visible = typeValue !== 'expression';
    },
  );

  if (!udfEditor || !editor) return null;
  const udf = udfEditor;

  return (
    <Observe
      render={() => (
        <DraggableResizableDialog
          defaultWidth="sm"
          classes={{ heading: classes.heading, childrenContainer: classes.childrenContainer }}
          DialogProps={{ dialog: editor, title: renderTitleFn(editor, udf), actions: [] }}
          DraggableResizableBoxProps={{ minWidth: '595px', minHeight: '250px' }}
        >
          <Observe
            render={() => (
              <div className={classes.root}>
                <div className={classes.left}>
                  <List component="nav">
                    {udf.items.map((i: ISelectable<IUdf>) => (
                      <ListItem selected={i.selected} key={i.id} button onClick={() => udf.selectItem(i)}>
                        <ListItemText primary={i.displayName} />
                      </ListItem>
                    ))}
                  </List>
                </div>
                <div className={classes.right}>
                  <DefaultEditFormView
                    editor={editor}
                    actions={['close', 'undo', 'save']}
                    classes={{ formButtonsRoot: classes.formButtonsRoot, formButtonsButton: classes.formButtonsButton }}
                  />
                </div>
              </div>
            )}
          />
        </DraggableResizableDialog>
      )}
    />
  );
};
